/**
 * Conexion del Campaign Studio con el motor real.
 *
 * El Studio ya no usa contenido fijo: deriva el briefing, la oportunidad y la
 * propuesta de campana de las respuestas reales de WHEN / WHO / WHAT y de la
 * inteligencia de demanda de Google Analytics. Lo unico que queda como
 * plantilla es el copy creativo, y se marca como simulado.
 *
 * Cada bloque dice de que datos sale:
 *   - real      -> el dato viene de una fuente medida (StatFin, GA, OSM)
 *   - simulated -> el bloque es plantilla o depende de magnitudes simuladas
 *   - mixed     -> el bloque mezcla dato real y simulado
 */
import {
  CAMPAIGN_PROPOSAL_MOCK,
  type CampaignProposalData,
  type DynamicBriefing,
  type OpportunityData,
  PRIORITY_OPTIONS,
} from '../data/campaignStudioData'
import { poiActivity } from './activityMap'
import type { AnalyticsInterest, Answer, Locale, Recommendation } from './types'

export type DataProvenance = 'real' | 'simulated' | 'mixed'

type L = Record<Locale, string>

const CONF_SHORT: Record<string, 'ALTA' | 'MEDIA'> = {
  high: 'ALTA',
  medium: 'MEDIA',
  low: 'MEDIA',
}

const CONF_FULL: Record<string, 'ALTA' | 'MEDIA' | 'BAJA'> = {
  high: 'ALTA',
  medium: 'MEDIA',
  low: 'BAJA',
}

const SEASON_LABEL: Record<string, L> = {
  winter_peak: { es: 'Pico de invierno', en: 'Winter peak', fi: 'Talvihuippu' },
  winter: { es: 'Temporada alta', en: 'High season', fi: 'Sesonki' },
  off_season: { es: 'Temporada baja', en: 'Low season', fi: 'Hiljainen kausi' },
  shoulder: { es: 'Temporada media', en: 'Shoulder season', fi: 'Välikausi' },
}

const COUNTRY_NAME: Record<string, L> = {
  FI: { es: 'Finlandia', en: 'Finland', fi: 'Suomi' },
  GB: { es: 'Reino Unido', en: 'United Kingdom', fi: 'Iso-Britannia' },
  DE: { es: 'Alemania', en: 'Germany', fi: 'Saksa' },
  FR: { es: 'Francia', en: 'France', fi: 'Ranska' },
  NL: { es: 'Países Bajos', en: 'Netherlands', fi: 'Alankomaat' },
  SE: { es: 'Suecia', en: 'Sweden', fi: 'Ruotsi' },
  CH: { es: 'Suiza', en: 'Switzerland', fi: 'Sveitsi' },
  US: { es: 'EE. UU.', en: 'United States', fi: 'Yhdysvallat' },
}

function full(es: string, en: string, fi: string): L {
  return { es, en, fi }
}

type Sector = 'accommodation' | 'activities' | 'gastronomy' | 'other'

const SECTOR_LABEL: Record<Sector, L> = {
  accommodation: full('Alojamiento', 'Accommodation', 'Majoitus'),
  activities: full('Actividades y experiencias', 'Activities & experiences', 'Aktiviteetit ja elämykset'),
  gastronomy: full('Restaurantes y gastronomía', 'Food & dining', 'Ravintolat ja ruoka'),
  other: full('Otros servicios', 'Other services', 'Muut palvelut'),
}

//: Sector del negocio a partir de su categoria/industria de OpenStreetMap.
function sectorOfPoi(poi?: { category?: string; industry?: string }): Sector {
  const a = poiActivity(poi?.category, poi?.industry)
  if (a === 'accommodation') return 'accommodation'
  if (a === 'food_drink') return 'gastronomy'
  if (a === 'activities' || a === 'info_practical' || a === 'services') return 'activities'
  return 'other'
}

function pick(value: L | undefined, locale: Locale): string {
  if (!value) return ''
  return value[locale] ?? value.en ?? value.es ?? ''
}

function marketName(country: string, marketName: L | undefined, locale: Locale): string {
  return pick(COUNTRY_NAME[country], locale) || pick(marketName, locale) || country
}

export function isSimulated(answer?: Answer): boolean {
  return Boolean(answer?.limitations?.some((l) => l.code === 'SIMULATED_DATA'))
}

/** Recoge los dataset_ids usados por un conjunto de respuestas. */
export function datasetsOf(...answers: (Answer | undefined)[]): string[] {
  const ids = new Set<string>()
  for (const a of answers) {
    if (!a) continue
    a.provenance_dataset_ids.forEach((d) => ids.add(d))
    a.markets.forEach((m) => m.dataset_ids.forEach((d) => ids.add(d)))
    a.windows.forEach((w) => w.dataset_ids.forEach((d) => ids.add(d)))
    a.recommendations.forEach((r) => r.provenance_dataset_ids.forEach((d) => ids.add(d)))
  }
  return Array.from(ids).sort()
}

import { interpretPrompt, type InterpretedQuery } from './studioInterpreter'

export interface WhyItem {
  key: string
  label: L
  value: L
  kind: DataProvenance
}

export interface StudioDerived {
  ready: boolean
  interpreted: InterpretedQuery
  briefing: DynamicBriefing
  opportunity: OpportunityData
  campaign: CampaignProposalData
  provenance: {
    audience: DataProvenance
    timing: DataProvenance
    product: DataProvenance
    copy: DataProvenance
  }
  sources: string[]
  primary: { name: string; sector: L }
  crossSell: { name: string; sector: L } | null
  whyChecklist: WhyItem[]
  budgetNote: L
}

export function deriveStudio(opts: {
  whenAns?: Answer
  whoAns?: Answer
  whatAns?: Answer
  interest?: AnalyticsInterest
  locale: Locale
  priorityId?: string | null
  query?: string
}): StudioDerived {
  const { whenAns, whoAns, whatAns, interest, locale, priorityId, query } = opts
  const ready = Boolean(whenAns && whoAns && whatAns)

  // 1. Interpretar la consulta libre del usuario
  const interpreted = interpretPrompt(query ?? '')

  // 2. Resolver MERCADO (WHO): Si el usuario especificó un país, seleccionamos ESE mercado
  let market = whoAns?.markets?.[0]
  if (interpreted.market && whoAns?.markets) {
    const matchedMarket = whoAns.markets.find((m) => m.country === interpreted.market!.value)
    if (matchedMarket) {
      market = matchedMarket
    }
  }

  // 3. Resolver TEMPORALIDAD / VENTANA (WHEN): Si el usuario especificó temporada o mes
  let window = whenAns?.windows?.[0]
  if (interpreted.season && whenAns?.windows) {
    const sKey = interpreted.season.value.seasonKey
    const sMonth = interpreted.season.value.month

    if (sKey === 'off_season') {
      const offWindow = whenAns.windows.find((w) => w.season.includes('off') || w.season.includes('shoulder'))
      if (offWindow) window = offWindow
    } else if (sMonth) {
      const monthStr = `${sMonth < 10 ? '0' : ''}${sMonth}`
      const matchedWindow = whenAns.windows.find((w) => w.start.includes(`-${monthStr}`) || w.end.includes(`-${monthStr}`))
      if (matchedWindow) window = matchedWindow
    }
  }

  // 4. Resolver PRODUCTO / POI (WHAT): Cruce por sector o palabra clave de producto
  const recs = whatAns?.recommendations ?? []
  let primary: Recommendation | undefined

  if (interpreted.product) {
    const pSector = interpreted.product.value.sector
    const pKey = interpreted.product.value.specificPoiKeyword

    if (pKey) {
      primary = recs.find((r) => {
        const text = `${r.poi.name} ${r.poi.category || ''} ${r.poi.industry || ''}`.toLowerCase()
        return text.includes(pKey)
      })
    }
    if (!primary) {
      primary = recs.find((r) => sectorOfPoi(r.poi) === pSector)
    }
  }

  if (!primary) {
    // Si no especificó producto pero sí mercado alemán en temporada baja, buscar qué POI encaja
    if (interpreted.market?.value === 'DE' || interpreted.season?.value.seasonKey === 'off_season') {
      primary = recs.find((r) => sectorOfPoi(r.poi) === 'accommodation') ?? recs[0]
    } else {
      primary = recs[0]
    }
  }

  const crossSell: Recommendation | null =
    recs.find((r) => primary && sectorOfPoi(r.poi) !== sectorOfPoi(primary.poi)) ?? null

  const country = market?.country ?? ''
  const mName = marketName(country, market?.name as L | undefined, locale)
  const sharePct = Math.round((market?.recommended_budget_share ?? market?.share ?? 0) * 100)
  const windowText = window ? `${window.start} → ${window.end}` : '—'
  const season = window?.season ?? 'winter'
  const seasonLabel = pick(SEASON_LABEL[season] ?? SEASON_LABEL.winter, locale)
  const activity = primary?.poi.name ?? '—'
  const primarySector = sectorOfPoi(primary?.poi)
  const primarySectorLabel = SECTOR_LABEL[primarySector]
  const crossSellSector = crossSell ? sectorOfPoi(crossSell.poi) : null
  const crossSellName = crossSell?.poi.name ?? null
  const crossSellLabel = crossSellSector ? SECTOR_LABEL[crossSellSector] : null

  // Google Analytics: solo el mercado lider (el que se recomienda).
  const gaCountry = interest?.countries?.find((c) => c.country === country)
  const gaActivity = gaCountry?.activities?.find((a) => a.category !== 'other')
  // Solo cuenta como "crecimiento" si es ESTE mercado el que sube; nunca se
  // atribuye a Finlandia el alza de otro pais (p. ej. Eslovaquia).
  const marketRising = interest?.rising?.find((r) => r.country === country)
  const gaTerms = (interest?.search_terms ?? []).slice(0, 4).map((s) => s.term)
  const convPct =
    gaCountry?.conversion_rate != null ? (gaCountry.conversion_rate * 100).toFixed(2) : null

  const provenance = {
    audience: (whoAns ? (isSimulated(whoAns) ? 'mixed' : 'real') : 'simulated') as DataProvenance,
    timing: (whenAns ? (isSimulated(whenAns) ? 'mixed' : 'real') : 'simulated') as DataProvenance,
    product: (whatAns ? (isSimulated(whatAns) ? 'simulated' : 'real') : 'simulated') as DataProvenance,
    copy: 'simulated' as DataProvenance,
  }

  const priority = PRIORITY_OPTIONS.find((p) => p.id === priorityId)
  const objectiveLabel: L = priority
    ? priority.label
    : full('Aumentar reservas', 'Increase bookings', 'Lisää varauksia')

  const briefing: DynamicBriefing = {
    objective: {
      label: objectiveLabel,
      detail: full(
        `Enfoque en ${seasonLabel.toLowerCase()}`,
        `Focus on ${seasonLabel.toLowerCase()}`,
        `Keskity: ${seasonLabel.toLowerCase()}`,
      ),
      status: 'definido',
      confidence: 'ALTA',
    },
    audience: {
      label: market?.name ? (market.name as L) : full(mName, mName, mName),
      detail: full(
        `${sharePct}% de la asignación recomendada`,
        `${sharePct}% of the recommended allocation`,
        `${sharePct}% suositellusta kohdennuksesta`,
      ),
      subdetail: gaActivity
        ? full(
            `Interés web: ${gaActivity.label} ${Math.round(gaActivity.share * 100)}%`,
            `Web interest: ${gaActivity.label} ${Math.round(gaActivity.share * 100)}%`,
            `Verkkokiinnostus: ${gaActivity.label} ${Math.round(gaActivity.share * 100)}%`,
          )
        : undefined,
      status: 'recomendado',
      confidence: CONF_SHORT[whoAns?.confidence ?? 'low'],
    },
    timing: {
      label: full(windowText, windowText, windowText),
      detail: full(seasonLabel, seasonLabel, seasonLabel),
      status: 'recomendado',
      confidence: CONF_SHORT[whenAns?.confidence ?? 'low'],
    },
    product: {
      label: full(activity, activity, activity),
      detail: full(
        pick(primarySectorLabel, 'es'),
        pick(primarySectorLabel, 'en'),
        pick(primarySectorLabel, 'fi'),
      ),
      status: 'recomendado',
      confidence: CONF_SHORT[whatAns?.confidence ?? 'low'],
    },
    channels: {
      label: full('La PYME elige el canal', 'The SME chooses the channel', 'Pk-yritys valitsee kanavan'),
      detail: full(
        'El piloto no prescribe plataformas publicitarias',
        'The pilot does not prescribe ad platforms',
        'Pilotti ei määrää mainosalustoja',
      ),
      status: 'pendiente',
    },
  }

  const whyChecklist: WhyItem[] = [
    {
      key: 'demand',
      label: full('Hay demanda', 'There is demand', 'Kysyntää on'),
      value: full(
        `${mName} es tu mercado más fuerte (${sharePct}% de la asignación recomendada)`,
        `${mName} is your strongest market (${sharePct}% of the recommended allocation)`,
        `${mName} on vahvin markkinasi (${sharePct}% suositellusta kohdennuksesta)`,
      ),
      kind: 'real',
    },
    {
      key: 'conversion',
      label: full('Convierte (medido)', 'It converts (measured)', 'Konvertoi (mitattu)'),
      value: convPct
        ? full(
            `Conversión del sitio del destino para ${mName}: ~${convPct}%`,
            `Destination site conversion for ${mName}: ~${convPct}%`,
            `Kohdesivuston konversio (${mName}): ~${convPct}%`,
          )
        : full('Sin dato de conversión', 'No conversion data', 'Ei konversiotietoa'),
      kind: convPct ? 'real' : 'simulated',
    },
    {
      key: 'growth',
      label: marketRising
        ? full('Está creciendo', 'It is growing', 'Kasvussa')
        : full('Mercado estable', 'Stable market', 'Vakaa markkina'),
      value: marketRising
        ? full(
            `El interés de ${mName} sube +${marketRising.change_pct}% en 2 semanas`,
            `${mName}'s interest is up +${marketRising.change_pct}% in 2 weeks`,
            `${mName}: kiinnostus +${marketRising.change_pct}% kahdessa viikossa`,
          )
        : full(
            `Sin subida fuerte de ${mName} esta quincena`,
            `No strong rise for ${mName} this fortnight`,
            `Ei voimakasta nousua (${mName}) tällä jaksolla`,
          ),
      kind: 'real',
    },
    {
      key: 'seasonality',
      label: full('Buen momento', 'Good timing', 'Hyvä ajoitus'),
      value: full(`${seasonLabel} (${windowText})`, `${seasonLabel} (${windowText})`, `${seasonLabel} (${windowText})`),
      kind: 'mixed',
    },
    {
      key: 'interest',
      label: full('Interés del visitante', 'Visitor interest', 'Kävijöiden kiinnostus'),
      value: gaActivity
        ? full(
            `Lo que más miran: ${gaActivity.label}`,
            `What they look at most: ${gaActivity.label}`,
            `Mitä he katsovat eniten: ${gaActivity.label}`,
          )
        : full('Sin dato de interés', 'No interest data', 'Ei kiinnostustietoa'),
      kind: gaActivity ? 'real' : 'simulated',
    },
    {
      key: 'offer',
      label: full('Tu oferta', 'Your offer', 'Tarjontasi'),
      value: primary
        ? full(
            `${activity} (${pick(primarySectorLabel, 'es')})`,
            `${activity} (${pick(primarySectorLabel, 'en')})`,
            `${activity} (${pick(primarySectorLabel, 'fi')})`,
          )
        : full('Sin negocio destacado', 'No highlighted business', 'Ei korostettua yritystä'),
      kind: 'simulated',
    },
  ]

  const budgetNote: L = full(
    'El reparto es una sugerencia: combina tamaño de mercado, interés web, conversión y encaje estacional. Tú decides cuánto invertir.',
    'The split is a suggestion: it combines market size, web interest, conversion and seasonal fit. You decide how much to invest.',
    'Jako on ehdotus: se yhdistää markkinan koko, verkkokiinnostus, konversio ja kausisopivuus. Sinä päätät, paljonko panostat.',
  )

  const reasons: Record<Locale, string[]> = {
    es: [
      `Mercado más fuerte: ${mName} (${sharePct}% de la asignación recomendada)`,
      `Mejor momento: ${windowText} (${seasonLabel})`,
      gaActivity
        ? `Lo que más miran en el sitio: ${gaActivity.label} (${Math.round(gaActivity.share * 100)}%)`
        : 'Pernoctaciones reales de Statistics Finland',
      crossSellName
        ? `Venta cruzada: ${crossSellName} (${pick(crossSellLabel!, 'es')})`
        : 'Oferta disponible en semanas valle',
    ],
    en: [
      `Strongest market: ${mName} (${sharePct}% of the recommended allocation)`,
      `Best timing: ${windowText} (${seasonLabel})`,
      gaActivity
        ? `What they look at most on the site: ${gaActivity.label} (${Math.round(gaActivity.share * 100)}%)`
        : 'Real nights from Statistics Finland',
      crossSellName
        ? `Cross-sell: ${crossSellName} (${pick(crossSellLabel!, 'en')})`
        : 'Available capacity in valley weeks',
    ],
    fi: [
      `Vahvin markkina: ${mName} (${sharePct}% suositellusta kohdennuksesta)`,
      `Paras ajoitus: ${windowText} (${seasonLabel})`,
      gaActivity
        ? `Mitä he katsovat eniten sivustolla: ${gaActivity.label} (${Math.round(gaActivity.share * 100)}%)`
        : 'Tilastokeskuksen todelliset yöpymiset',
      crossSellName
        ? `Lisämyynti: ${crossSellName} (${pick(crossSellLabel!, 'fi')})`
        : 'Kapasiteettia saatavilla hiljaisina viikkoina',
    ],
  }

  const sources: string[] = Array.from(
    new Set([
      ...datasetsOf(whenAns, whoAns, whatAns),
      ...(interest ? ['visitlevi_ga_country (Google Analytics)'] : []),
    ]),
  )

  const opportunity: OpportunityData = {
    title: {
      es: `${mName} · ${seasonLabel}`,
      en: `${mName} · ${seasonLabel}`,
      fi: `${mName} · ${seasonLabel}`,
    },
    subtitle: windowText,
    recommendation: {
      es: `En ${mName}, durante ${windowText}, la mejor oportunidad es ${activity} (${pick(primarySectorLabel, 'es')}).${crossSellName ? ` Venta cruzada: ${crossSellName} (${pick(crossSellLabel!, 'es')}).` : ''}`,
      en: `In ${mName}, during ${windowText}, the best opportunity is ${activity} (${pick(primarySectorLabel, 'en')}).${crossSellName ? ` Cross-sell: ${crossSellName} (${pick(crossSellLabel!, 'en')}).` : ''}`,
      fi: `Kohteessa ${mName}, ajanjaksolla ${windowText}, paras mahdollisuus on ${activity} (${pick(primarySectorLabel, 'fi')}).${crossSellName ? ` Lisämyynti: ${crossSellName} (${pick(crossSellLabel!, 'fi')}).` : ''}`,
    },
    potential: whoAns?.confidence === 'high' ? 'ALTO' : 'MEDIO',
    confidence: CONF_SHORT[whoAns?.confidence ?? 'low'],
    reasons,
    why: {
      title: {
        es: `¿Por qué ${activity}?`,
        en: `Why ${activity}?`,
        fi: `Miksi ${activity}?`,
      },
      subtitle: {
        es: 'Motivos, en lenguaje claro',
        en: 'Reasons, in plain language',
        fi: 'Perustelut selkokielellä',
      },
      points: reasons,
      sources,
      confidence: CONF_FULL[whoAns?.confidence ?? 'low'],
      disclaimer: {
        es: 'Mercado y momento: datos medidos. La puntuación de cada negocio es una estimación del modelo (simulada).',
        en: 'Market and timing: measured data. Each business score is a model estimate (simulated).',
        fi: 'Markkina ja ajoitus: mitattua dataa. Yritysten pisteet ovat mallin arvio (simuloitu).',
      },
    },
  }

  // Campana: campos reales + copy de plantilla (marcado como simulado).
  const term = gaTerms[0] ?? activity.toLowerCase()
  const campaign: CampaignProposalData = {
    ...CAMPAIGN_PROPOSAL_MOCK,
    title: `${mName} · ${seasonLabel}`,
    subtitle: {
      es: 'Propuesta basada en los datos del destino',
      en: 'Proposal based on destination data',
      fi: 'Ehdotus kohteen datan pohjalta',
    },
    objective: {
      es: `Aumentar reservas de ${activity} en ${seasonLabel.toLowerCase()}.`,
      en: `Increase bookings for ${activity} in the ${seasonLabel.toLowerCase()}.`,
      fi: `Lisää ${activity} -varauksia ${seasonLabel.toLowerCase()}.`,
    },
    audience: {
      es: `Viajeros de ${mName} interesados en ${pick(primarySectorLabel, 'es').toLowerCase()}.`,
      en: `Travellers from ${mName} interested in ${pick(primarySectorLabel, 'en').toLowerCase()}.`,
      fi: `Matkailijat maasta ${mName}, kiinnostuneita: ${pick(primarySectorLabel, 'fi').toLowerCase()}.`,
    },
    period: windowText,
    creativeConcept: `${seasonLabel} in Levi`,
    mainMessage: `${activity} — ${term}`,
    cta: CAMPAIGN_PROPOSAL_MOCK.cta,
    contents: CAMPAIGN_PROPOSAL_MOCK.contents.map((c) => ({
      ...c,
      body: `${c.body} (${mName} · ${seasonLabel})`,
    })),
  }

  return {
    ready,
    interpreted,
    briefing,
    opportunity,
    campaign,
    provenance,
    sources,
    primary: { name: activity, sector: primarySectorLabel },
    crossSell: crossSell && crossSellLabel ? { name: crossSellName!, sector: crossSellLabel } : null,
    whyChecklist,
    budgetNote,
  }
}
